import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import Logo from "../ui/Logo";
import {
  LayoutDashboard,
  FileSearch,
  Eye,
  BarChart3,
  Sparkles,
  FlaskConical,
  Swords,
  Crown,
  LogOut,
  ChevronLeft,
  ChevronRight,
  User,
  Microscope,
  Lock,
} from "lucide-react";
import { PREMIUM_FEATURE_PAGE_MAP, canAccessFeature, getMinimumPlanForFeature } from "../../utils/featureAccess";

const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
      { id: "executive", label: "Executive Summary", icon: Crown },
    ],
  },
  {
    label: "Analysis",
    items: [
      { id: "audits", label: "Page Audits", icon: FileSearch },
      { id: "ai-visibility-lab", label: "AI Visibility Lab", icon: Microscope },
      { id: "advanced", label: "Advanced Audit", icon: Sparkles },
    ],
  },
  {
    label: "Strategy",
    items: [
      { id: "simulator", label: "Strategy Simulator", icon: FlaskConical },
      { id: "compare", label: "Competitor Intel", icon: Swords },
    ],
  },
  {
    label: "Tools",
    items: [
      { id: "cli", label: "CLI Tool", icon: Microscope },
    ],
  },
  {
    label: "Infrastructure",
    items: [
      { id: "monitor", label: "Monitor Pages", icon: Eye },
      { id: "reports", label: "Reports", icon: BarChart3 },
    ],
  },
];

const SIDEBAR_ITEMS_ORDER = [
  "brand",
  "header-Overview",
  "dashboard",
  "executive",
  "header-Analysis",
  "audits",
  "ai-visibility-lab",
  "advanced",
  "header-Strategy",
  "simulator",
  "compare",
  "header-Tools",
  "cli",
  "header-Infrastructure",
  "monitor",
  "reports",
  "profile",
  "logout"
];

const getSidebarItemIndex = (id) => {
  const idx = SIDEBAR_ITEMS_ORDER.indexOf(id);
  return idx === -1 ? 0 : idx;
};

const sidebarContainerVariants = {
  hidden: { opacity: 1 },
  show: { opacity: 1 },
};

const sidebarItemVariants = (index, reduceMotion) => {
  if (reduceMotion) return {};
  return {
    hidden: { opacity: 0, x: -32 },
    show: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 100, // Higher stiffness for snappier bounce
        damping: 8,     // Lower damping for more prominent jiggle/overshoot
        mass: 0.8,
        delay: index * 0.14, // Slower sequential stagger
      },
    },
  };
};

export default function Sidebar({ activePage, onNavigate, onFeatureLocked, onLogout }) {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const reduceMotion = useReducedMotion();

  const containerVariants = reduceMotion ? {} : sidebarContainerVariants;


  return (
    <aside
      data-testid="sidebar"
      className={`fixed top-0 left-0 h-screen flex flex-col z-40 transition-all duration-300 ${collapsed ? "w-16" : "w-[220px]"
        }`}
      style={{
        background: "var(--surface)",
        borderRight: "1px solid var(--border)",
      }}
    >
      <motion.div
        className="h-full flex flex-col"
        initial="hidden"
        animate="show"
        variants={containerVariants}
      >
        {/* Logo / Brand */}
        <motion.div
          variants={sidebarItemVariants(getSidebarItemIndex("brand"), reduceMotion)}
          className={`h-14 flex items-center shrink-0 cursor-pointer hover:opacity-80 transition-opacity ${collapsed ? "justify-center px-0" : "gap-2.5 px-4"
            }`}
          style={{ borderBottom: "1px solid var(--border)" }}
          onClick={() => onNavigate("landing")}
          title="Go to home"
        >
          <Logo size="sm" />
          {!collapsed && (
            <span className="brand-name font-bold text-sm tracking-tight text-white whitespace-nowrap">
              Scopuz
            </span>
          )}
        </motion.div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {NAV_SECTIONS.map((section, sectionIdx) => (
            <div key={section.label}>
              {/* Gradient section divider between groups */}
              {sectionIdx > 0 && <div className="sidebar-section-divider" />}
              {!collapsed && (
                <motion.p
                  variants={sidebarItemVariants(getSidebarItemIndex(`header-${section.label}`), reduceMotion)}
                  className="px-3 mb-1 text-[12px] font-bold uppercase tracking-widest"
                  style={{ color: "var(--text-subtle)" }}
                >
                  {section.label}
                </motion.p>
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activePage === item.id;
                  const premiumFeature = PREMIUM_FEATURE_PAGE_MAP[item.id];
                  const isPremiumLocked =
                    Boolean(premiumFeature) && !canAccessFeature(user, premiumFeature);
                  return (
                    <motion.button
                      variants={sidebarItemVariants(getSidebarItemIndex(item.id), reduceMotion)}
                      whileHover={reduceMotion ? {} : { x: isActive ? 0 : 2 }}
                      key={item.id}
                      data-testid={`nav-${item.id}`}
                      onClick={() => {
                        if (isPremiumLocked) {
                          onFeatureLocked?.(item.id);
                          return;
                        }
                        onNavigate(item.id);
                      }}
                      title={
                        collapsed
                          ? item.label
                          : isPremiumLocked
                            ? `Available in ${getMinimumPlanForFeature(premiumFeature)}`
                            : undefined
                      }
                      className={`sidebar-nav-item${isActive ? " sidebar-nav-active" : ""}${collapsed ? " sidebar-collapsed" : ""
                        }`}
                      style={isPremiumLocked ? { opacity: 0.62 } : undefined}
                    >
                      <Icon className="sidebar-nav-icon" />
                      {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}

                      {isPremiumLocked && (
                        <Lock
                          className="ml-auto shrink-0"
                          style={{ width: "13px", height: "13px", color: "var(--primary)" }}
                          aria-label={`Available in ${getMinimumPlanForFeature(premiumFeature)}`}
                        />
                      )}
                      {/* Tooltip for collapsed */}
                      {collapsed && <span className="sidebar-tooltip">{item.label}</span>}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer: profile + logout + collapse */}
        <div className="shrink-0 p-2 space-y-0.5">
          <div className="sidebar-section-divider" style={{ marginBottom: "0.5rem" }} />
          <motion.button
            variants={sidebarItemVariants(getSidebarItemIndex("profile"), reduceMotion)}
            whileHover={reduceMotion ? {} : { x: activePage === "profile" ? 0 : 2 }}
            data-testid="nav-profile"
            onClick={() => onNavigate("profile")}
            className={`sidebar-nav-item${activePage === "profile" ? " sidebar-nav-active" : ""}${collapsed ? " sidebar-collapsed" : ""
              }`}
          >
            <User className="sidebar-nav-icon" />
            {!collapsed && <span>Profile</span>}
          </motion.button>

          <motion.button
            variants={sidebarItemVariants(getSidebarItemIndex("logout"), reduceMotion)}
            whileHover={reduceMotion ? {} : { x: 2 }}
            data-testid="logout-button"
            onClick={onLogout}
            className={`sidebar-nav-item${collapsed ? " sidebar-collapsed" : ""}`}
            style={{ color: "var(--text-muted)" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#f87171";
              e.currentTarget.style.background = "rgba(239, 68, 68, 0.08)";
              e.currentTarget.style.borderLeftColor = "rgba(239, 68, 68, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "var(--text-muted)";
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderLeftColor = "transparent";
            }}
          >
            <LogOut className="sidebar-nav-icon" />
            {!collapsed && <span>Sign Out</span>}
          </motion.button>

          {/* User email */}
          {!collapsed && user && (
            <div className="px-3 pt-2 pb-1">
              <p className="text-[11px] truncate" style={{ color: "var(--text-subtle)" }}>
                {user.email}
              </p>
            </div>
          )}

          {/* Collapse toggle */}
          <button
            data-testid="sidebar-collapse-toggle"
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center py-1.5 rounded-md transition-colors duration-150"
            style={{ color: "var(--text-subtle)" }}
          >
            {collapsed ? (
              <ChevronRight style={{ width: "14px", height: "14px" }} />
            ) : (
              <ChevronLeft style={{ width: "14px", height: "14px" }} />
            )}
          </button>
        </div>
      </motion.div>
    </aside>
  );
}

